import React, { useState, useMemo, useEffect } from 'react';
import { 
  Search, 
  SlidersHorizontal, 
  ChevronDown, 
  Sparkles, 
  ExternalLink, 
  ShieldCheck, 
  AlertTriangle, 
  Info, 
  Cpu, 
  Terminal, 
  CheckCircle2, 
  TrendingUp, 
  X, 
  Download, 
  CreditCard, 
  Plus, 
  Eye, 
  DollarSign, 
  Layers, 
  FileCheck, 
  FolderOpen, 
  Bookmark, 
  Zap, 
  Send, 
  BookOpen,
  Check,
  Building,
  HelpCircle
} from 'lucide-react';
import { DigitalProduct } from '../data/nexusDigitalProducts';

interface NexusDigitalAssetLibraryProps {
  user: any;
  products: DigitalProduct[];
  setProducts: React.Dispatch<React.SetStateAction<DigitalProduct[]>>;
  activeLibraryTab: string;
  setActiveLibraryTab: (tab: string) => void;
  onAddLog: (module: string, action: string, status: string, details: string) => void;
  onAddTaskToWorkSuite?: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
}

export const NexusDigitalAssetLibrary: React.FC<NexusDigitalAssetLibraryProps> = ({
  user,
  products,
  setProducts,
  activeLibraryTab,
  setActiveLibraryTab,
  onAddLog,
  onAddTaskToWorkSuite
}) => {
  // --- STATE ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState<string>('ALL');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('ALL');
  const [selectedLicence, setSelectedLicence] = useState<string>('ALL');
  const [selectedSourceType, setSelectedSourceType] = useState<string>('ALL');
  const [maxPrice, setMaxPrice] = useState<number>(300);
  const [onlyManagedLaunch, setOnlyManagedLaunch] = useState(false);
  const [onlyOriginals, setOnlyOriginals] = useState(false);
  const [includeExternal, setIncludeExternal] = useState(true);
  const [sortBy, setSortBy] = useState<string>('priority');
  
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    const saved = localStorage.getItem('nexorax_asset_watchlist');
    return saved ? JSON.parse(saved) : [];
  });

  // Modal / PDP states
  const [selectedAsset, setSelectedAsset] = useState<DigitalProduct | null>(null);

  // Client Deliveries
  const [clientDeliveries, setClientDeliveries] = useState<any[]>(() => {
    const saved = localStorage.getItem('nexorax_client_deliveries');
    return saved ? JSON.parse(saved) : [];
  });

  // Admin and Import panels
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminMode, setAdminMode] = useState<'add' | 'import' | 'generator'>('add');
  const [adminSuccessMsg, setAdminSuccessMsg] = useState('');
  
  // Custom Asset Creator Forms
  const [newAssetForm, setNewAssetForm] = useState({
    assetName: '',
    assetType: 'Website Template' as any,
    category: 'React Templates',
    shortDescription: '',
    fullDescription: '',
    previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
    productScreenshots: [] as string[],
    fileFormats: [] as string[],
    includedFiles: [] as string[],
    techStack: [] as string[],
    integrations: [] as string[],
    platform: 'React / Vite',
    licenceType: 'Business Use',
    deliveryMethod: 'Client Dashboard Delivery',
    setupDifficulty: 'Intermediate' as any,
    standardPrice: 99,
    managedLaunchPrice: 59,
    monthlySupportPrice: 19,
    discountEligible: true,
    sourceType: 'Original NexoraX Asset' as any,
    licenceProofStatus: 'Original Asset' as any,
    assetStatus: 'Published' as any,
    approvalStatus: 'Approved for Sale' as any,
    featured: false,
    tags: [] as string[],
    // Admin only private fields
    internalBuildNotes: '',
    licenceNotes: '',
    supplierNotes: '',
    internalCost: 30,
    marginEstimate: 60,
    fileStorageNotes: 'Secure Google Drive NexoraX Library Sync',
    privateSourceLinks: '',
    legalReviewNotes: 'Auto-approved compliance review'
  });

  const [externalImportForm, setExternalImportForm] = useState({
    marketplaceName: 'UI8 Marketplace',
    assetTitle: '',
    externalUrl: '',
    category: 'Design Assets',
    assetType: 'UI Kit' as any,
    notes: '',
    licenceStatus: 'Licence Required' as any,
    affiliateStatus: 'Non-Affiliate Reference' as any,
    publicDisplayStatus: 'Approved for Preview' as any
  });

  // --- SAVE SYNC ---
  useEffect(() => {
    localStorage.getItem('nexorax_client_deliveries');
    localStorage.setItem('nexorax_client_deliveries', JSON.stringify(clientDeliveries));
  }, [clientDeliveries]);

  useEffect(() => {
    localStorage.setItem('nexorax_asset_watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  // --- HELPERS ---
  const toggleWatchlist = (id: string, name: string) => {
    let updated;
    if (watchlist.includes(id)) {
      updated = watchlist.filter(item => item !== id);
      onAddLog('ASSET_LIBRARY', `Removed from saved wishlist: ${name}`, 'SUCCESS', `Asset ${id} removed.`);
    } else {
      updated = [...watchlist, id];
      onAddLog('ASSET_LIBRARY', `Saved to watchlist: ${name}`, 'SUCCESS', `Asset ${id} bookmarked.`);
    }
    setWatchlist(updated);
  };

  const handleRequestSetupInquiry = (asset: DigitalProduct) => {
    const isReferenceOnly = asset.sourceType === "External Marketplace Link" || asset.sourceType === "Affiliate Marketplace Link";
    if (isReferenceOnly) {
      window.open(asset.externalSourceUrl || 'https://themeforest.net', '_blank');
      onAddLog('ASSET_LIBRARY', `Redirected external asset link: ${asset.productName}`, 'WARNING', `External procurement link triggered. Local files protected.`);
      return;
    }

    const newDelivery = {
      id: `delivery-${Date.now()}`,
      assetId: asset.id,
      assetName: asset.productName,
      assetType: asset.assetType || asset.productType,
      requestedAt: new Date().toISOString(),
      deliveryStatus: 'Request Received',
      licenceType: asset.licenceType,
      filesAccessStatus: 'Pending Verification',
      setupChecklist: [
        { label: 'Register B2B Workspace parameters', done: false },
        { label: 'Map domain name dns protocols', done: false },
        { label: 'Vite server continuous setup deployment', done: false },
        { label: 'Independent code package audit testing', done: false }
      ],
      managedLaunchStatus: asset.discountEligible ? 'Eligible - Discount Applied' : 'Standard Baseline setup',
      invoiceStatus: 'Invoice Outstanding - Quote Emailed',
      supportStatus: 'Inactive - Pending setup launch',
      nextAction: 'Confirm setup configuration in verification workspace call'
    };

    const updated = [newDelivery, ...clientDeliveries];
    setClientDeliveries(updated);
    
    onAddLog('CLIENT_DESPATCH', `Initialized checkout delivery route: ${asset.productName}`, 'SUCCESS', `Workspace delivery dispatch created for @nexorax.co.uk.`);
    
    // Dispatch custom event to sync with CRM in parent
    window.dispatchEvent(new CustomEvent('nexorax-trigger-tab-change', { detail: 'Dashboard' }));
    
    // Smooth scroll to client deliveries container or alert success
    setTimeout(() => {
      const el = document.getElementById('client-asset-deliveries-hub');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 150);

    alert(`CONGRATULATIONS!\n\nYour setup and checkout request for "${asset.productName}" has been successfully queued!\n\nNext steps:\n1. Your Client Dashboard now includes a tracking route with statuses.\n2. Invoice and setup schedule has been routed through @nexorax.co.uk.\n3. Human operator will call direct lines within standard SLA hours.`);
  };

  const handleAddCustomAsset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAssetForm.assetName) {
      alert("Asset name is mandatory!");
      return;
    }

    const price = Number(newAssetForm.standardPrice) || 0;
    const sCost = Number(newAssetForm.internalCost) || 0;
    const mMargin = Number(newAssetForm.marginEstimate) || 0;

    const added: DigitalProduct = {
      id: `custom-asset-${Date.now()}`,
      productName: newAssetForm.assetName,
      productType: 'Social Media Pack', // Maps standard required option
      assetName: newAssetForm.assetName,
      assetType: newAssetForm.assetType,
      category: newAssetForm.category,
      shortDescription: newAssetForm.shortDescription,
      fullDescription: newAssetForm.fullDescription,
      previewImage: newAssetForm.previewImage,
      productScreenshots: newAssetForm.productScreenshots.length > 0 ? newAssetForm.productScreenshots : [newAssetForm.previewImage],
      includedFiles: newAssetForm.includedFiles.length > 0 ? newAssetForm.includedFiles : ['index.html', 'readme.md'],
      fileFormats: newAssetForm.fileFormats.length > 0 ? newAssetForm.fileFormats : ['.fig'],
      techStack: newAssetForm.techStack.length > 0 ? newAssetForm.techStack : ['Figma', 'React'],
      integrations: newAssetForm.integrations,
      platform: newAssetForm.platform,
      setupDifficulty: newAssetForm.setupDifficulty,
      deliveryMethod: newAssetForm.deliveryMethod as any,
      licenceType: newAssetForm.licenceType as any,
      setupPrice: newAssetForm.managedLaunchPrice,
      productPrice: price,
      monthlySupportPrice: newAssetForm.monthlySupportPrice,
      paymentStatus: 'Request Invoice',
      checkoutStatus: 'Invoice Only',
      productStatus: newAssetForm.assetStatus,
      buildStatus: 'Template Ready',
      approvalStatus: newAssetForm.approvalStatus,
      tags: newAssetForm.tags.length > 0 ? newAssetForm.tags : ['Custom', 'Nexus Asset'],
      featured: newAssetForm.featured,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      discountEligible: newAssetForm.discountEligible,
      sourceType: newAssetForm.sourceType,
      externalSourceUrl: newAssetForm.privateSourceLinks,
      licenceProofStatus: newAssetForm.licenceProofStatus,
      // Private fields
      internalCost: sCost,
      marginEstimate: mMargin,
      buildNotes: newAssetForm.internalBuildNotes,
      privateLicenceNotes: newAssetForm.licenceNotes,
      supplierNotes: newAssetForm.supplierNotes,
      fileStorageNotes: newAssetForm.fileStorageNotes
    };

    const updated = [added, ...products];
    setProducts(updated);

    onAddLog('ADMIN_PRODUCTS', `Published asset to Library: ${added.productName}`, 'SUCCESS', `Asset ${added.id} added with Standard Price £${added.productPrice}.`);
    
    if (onAddTaskToWorkSuite) {
      onAddTaskToWorkSuite(`Review and deployment verify newly published asset: ${added.productName}`, 'MEDIUM');
    }

    setAdminSuccessMsg(`Successfully added "${added.productName}" to the Nexus Digital Asset Library catalog! Status is set to ${added.productStatus}.`);
    
    // Reset form fields
    setNewAssetForm({
      assetName: '',
      assetType: 'Website Template',
      category: 'React Templates',
      shortDescription: '',
      fullDescription: '',
      previewImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800',
      productScreenshots: [],
      fileFormats: [],
      includedFiles: [],
      techStack: [],
      integrations: [],
      platform: 'React / Vite',
      licenceType: 'Business Use',
      deliveryMethod: 'Client Dashboard Delivery',
      setupDifficulty: 'Intermediate',
      standardPrice: 99,
      managedLaunchPrice: 59,
      monthlySupportPrice: 19,
      discountEligible: true,
      sourceType: 'Original NexoraX Asset',
      licenceProofStatus: 'Original Asset',
      assetStatus: 'Published',
      approvalStatus: 'Approved for Sale',
      featured: false,
      tags: [],
      internalBuildNotes: '',
      licenceNotes: '',
      supplierNotes: '',
      internalCost: 30,
      marginEstimate: 60,
      fileStorageNotes: 'Secure Google Drive NexoraX Library Sync',
      privateSourceLinks: '',
      legalReviewNotes: 'Auto-approved compliance review'
    });

    setTimeout(() => {
      setAdminSuccessMsg('');
    }, 4000);
  };

  const handleExternalImportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!externalImportForm.assetTitle || !externalImportForm.externalUrl) {
      alert("External Asset URL and Title parameters are mandatory!");
      return;
    }

    const price = 49; // Default reference baseline
    const referenceAsset: DigitalProduct = {
      id: `ref-asset-${Date.now()}`,
      productName: externalImportForm.assetTitle,
      productType: 'Ready-Made Website',
      assetName: externalImportForm.assetTitle,
      assetType: externalImportForm.assetType,
      category: externalImportForm.category,
      shortDescription: `${externalImportForm.marketplaceName} index asset. NOTES: ${externalImportForm.notes || 'No description notes provided'}`,
      fullDescription: `DISCLOSURE DIRECTIVE: This asset is listed strictly as an external marketplace reference. Purchase, file license terms, and support warranties are administered directly by ${externalImportForm.marketplaceName}. Local file downloads are disabled to preserve legal compliance.`,
      previewImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      productScreenshots: [],
      includedFiles: [`External file collection hosted on ${externalImportForm.marketplaceName}`],
      fileFormats: ['.fig', '.psd', '.sketch'],
      techStack: ['Design Toolkit Assets', 'HTML Baseline'],
      integrations: [],
      platform: externalImportForm.marketplaceName,
      setupDifficulty: 'Intermediate',
      deliveryMethod: 'Manual Setup Required',
      licenceType: 'Personal Use',
      setupPrice: 199,
      productPrice: price,
      monthlySupportPrice: 0,
      paymentStatus: 'Quote Required',
      checkoutStatus: 'Not Connected',
      productStatus: 'Published',
      buildStatus: 'Public Preview Ready',
      approvalStatus: 'External Link Only',
      tags: ['External', 'Affiliate', externalImportForm.marketplaceName, 'Reference'],
      featured: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      discountEligible: false,
      sourceType: externalImportForm.affiliateStatus === 'Affiliate Marketplace Reference' ? 'Affiliate Marketplace Link' : 'External Marketplace Link',
      externalSourceUrl: externalImportForm.externalUrl,
      licenceProofStatus: externalImportForm.licenceStatus,
      // Private fields
      internalCost: 0,
      marginEstimate: 0,
      buildNotes: `Listed reference item from ${externalImportForm.marketplaceName}`,
      privateLicenceNotes: `Third party license via ${externalImportForm.marketplaceName}`
    };

    const updated = [referenceAsset, ...products];
    setProducts(updated);

    onAddLog('ADMIN_PRODUCTS', `Imported External Reference: ${referenceAsset.productName}`, 'SUCCESS', `Stored reference link URL: ${referenceAsset.externalSourceUrl}`);

    setAdminSuccessMsg(`Successfully cataloged external asset "${referenceAsset.productName}" inside the references matrix list.`);
    
    setExternalImportForm({
      marketplaceName: 'UI8 Marketplace',
      assetTitle: '',
      externalUrl: '',
      category: 'Design Assets',
      assetType: 'UI Kit',
      notes: '',
      licenceStatus: 'Licence Required',
      affiliateStatus: 'Non-Affiliate Reference',
      publicDisplayStatus: 'Approved for Preview'
    });

    setTimeout(() => {
      setAdminSuccessMsg('');
    }, 4000);
  };

  const handleSimulateAiGenerator = (type: string) => {
    onAddLog('AI_GENERATION', `Draft auto-generator initialized: ${type}`, 'SUCCESS', 'Vanguard Neural engine spinning container ports.');
    
    // Simulate compilation of a draft asset
    const randomId = Math.floor(Math.random() * 1000);
    const draft: DigitalProduct = {
      id: `ai-draft-${Date.now()}`,
      productName: `Neural Draft: Modern ${type} ${randomId}`,
      productType: 'Social Media Pack',
      assetName: `Neural Draft: Modern ${type} ${randomId}`,
      assetType: type as any,
      category: 'AI Operational Drafts',
      shortDescription: `A premium design asset wireframe compiled automatically by the NexoraX AI Core generator queue.`,
      fullDescription: `AUTHENTIC AI DRAFT: Compiled on demand inside the virtual node platform environment. This asset is marked as a Draft for security verification. Human operators must review code structure, API calls, and compliance policies before authorizing for preview or active catalog sale.`,
      previewImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=800',
      productScreenshots: [],
      includedFiles: ['ai_output_compiled.json', 'baseline.css'],
      fileFormats: ['.fig', '.json'],
      techStack: ['AI-Draft Output', 'Vite Skeleton', 'Tailwind Base'],
      integrations: [],
      platform: 'Vanguard Sandbox Output',
      setupDifficulty: 'Beginner',
      deliveryMethod: 'Client Dashboard Delivery',
      licenceType: 'Personal Use',
      setupPrice: 0,
      productPrice: 49,
      monthlySupportPrice: 0,
      paymentStatus: 'Free Preview',
      checkoutStatus: 'Not Connected',
      productStatus: 'Draft', // Draft only
      buildStatus: 'Needs Testing',
      approvalStatus: 'Admin Draft', // Admin Draft Only
      tags: ['AI Generated', 'Draft Only', type],
      featured: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sourceType: 'Draft AI-Generated Asset',
      licenceProofStatus: 'Needs Review',
      discountEligible: true,
      internalCost: 0,
      marginEstimate: 100
    };

    const updated = [draft, ...products];
    setProducts(updated);

    alert(`AI COMPILER COMPLETED!\n\nAI Generator built draft of "${draft.productName}".\n\nNote: In accordance with Lead District compliance metrics, this asset is created as a "DRAFT" under approvalStatus "Admin Draft". It remains invisible to public visitors until you review and publish it from the Admin Asset Tracker.`);
  };

  // --- FILTER & SORT EXECUTION ---
  const filteredAssets = useMemo(() => {
    let result = products.filter(asset => {
      // 1. Tag or Category Filter based on active tab!
      if (activeLibraryTab !== 'Digital Asset Library' && activeLibraryTab !== 'Digital Product Store') {
        const tabLower = activeLibraryTab.toLowerCase();
        const aType = (asset.assetType || '').toLowerCase();
        
        if (tabLower === 'website templates' && aType !== 'website template') return false;
        if (tabLower === 'app templates' && aType !== 'mobile app template') return false;
        if (tabLower === 'dashboard templates' && aType !== 'dashboard template') return false;
        if (tabLower === 'ui kits' && aType !== 'ui kit' && aType !== 'figma file' && aType !== 'adobe xd file' && aType !== 'sketch file') return false;
        if (tabLower === 'slide decks' && aType !== 'slide deck') return false;
        if (tabLower === 'code packs' && aType !== 'code pack') return false;
        if (tabLower === 'workflow templates' && aType !== 'workflow template' && !asset.productType.includes('Workflow')) return false;
        if (tabLower === 'brand & social packs' && aType !== 'brand kit' && aType !== 'social pack' && aType !== 'business pack' && !asset.productType.includes('Social')) return false;
      }

      // If Digital Product Store is selected, we filter to show items that are NOT external only links by default
      if (activeLibraryTab === 'Digital Product Store' && asset.approvalStatus === 'External Link Only') {
        return false;
      }

      // 2. Search terminology keyword mapping
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesName = (asset.productName || '').toLowerCase().includes(term) || (asset.assetName || '').toLowerCase().includes(term);
        const matchesDesc = (asset.shortDescription || '').toLowerCase().includes(term) || (asset.fullDescription || '').toLowerCase().includes(term);
        const matchesTags = asset.tags?.some(tag => tag.toLowerCase().includes(term)) || false;
        const matchesTech = asset.techStack?.some(tech => tech.toLowerCase().includes(term)) || false;
        const matchesPlatform = (asset.platform || '').toLowerCase().includes(term);
        if (!matchesName && !matchesDesc && !matchesTags && !matchesTech && !matchesPlatform) {
          return false;
        }
      }

      // 3. Multi-Dropdown specific details checking
      if (selectedAssetType !== 'ALL') {
        const typeMatch = (asset.assetType || asset.productType) === selectedAssetType;
        if (!typeMatch) return false;
      }

      if (selectedPlatform !== 'ALL' && asset.platform !== selectedPlatform) {
        return false;
      }

      if (selectedLicence !== 'ALL' && asset.licenceType !== selectedLicence) {
        return false;
      }

      if (selectedSourceType !== 'ALL' && asset.sourceType !== selectedSourceType) {
        return false;
      }

      // Price mapping
      const pCost = asset.productPrice || asset.standardPrice || 0;
      if (pCost > maxPrice) return false;

      // Managed Launch criteria
      if (onlyManagedLaunch && !asset.discountEligible) return false;

      // Originals only
      if (onlyOriginals && asset.sourceType !== 'Original NexoraX Asset') return false;

      // Hide external assets if unchecked
      if (!includeExternal && (asset.sourceType === 'External Marketplace Link' || asset.sourceType === 'Affiliate Marketplace Link')) {
        return false;
      }

      // Hide drafts from general public (Admins can see everything)
      if (asset.approvalStatus === 'Admin Draft' && !showAdminPanel) {
        return false;
      }

      return true;
    });

    // 4. Sort execution
    return [...result].sort((a, b) => {
      if (sortBy === 'name') {
        return a.productName.localeCompare(b.productName);
      }
      if (sortBy === 'latest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'price_low') {
        return (a.productPrice || 0) - (b.productPrice || 0);
      }
      if (sortBy === 'price_high') {
        return (b.productPrice || 0) - (a.productPrice || 0);
      }
      if (sortBy === 'priority') {
        // Featured and priority index sorting
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return 0;
    });
  }, [products, activeLibraryTab, searchTerm, selectedAssetType, selectedPlatform, selectedLicence, selectedSourceType, maxPrice, onlyManagedLaunch, onlyOriginals, includeExternal, sortBy, showAdminPanel]);

  // Dynamic values for dropdown categories
  const platformsList = useMemo(() => {
    const list = new Set<string>();
    products.forEach(p => { if (p.platform) list.add(p.platform); });
    return Array.from(list);
  }, [products]);

  return (
    <div className="space-y-8 text-white font-sans antialiased">
      
      {/* 1. DISCLOSURE WARNING BANNER */}
      <div className="bg-[#030612]/90 border border-amber-500/20 rounded-2xl p-5 shadow-[0_0_20px_rgba(245,158,11,0.05)] border-l-4 border-l-amber-500 flex flex-col md:flex-row gap-4 items-start">
        <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 animate-pulse mt-0.5" />
        <div className="space-y-1.5 text-xs text-zinc-400 normal-case leading-relaxed">
          <span className="text-amber-400 font-mono font-black uppercase text-[10px] tracking-wider block">
            🛡️ B2B COMPLIANCE &amp; IMPLEMENTATION POLICIES (OFFICE OF THE DIRECTOR MIGRATION MANDATE):
          </span>
          <p>
            Ready-made workflow packages, templates, and script libraries require active account configuration adjustments, API key permissions management, and sandboxed deployment testing before production ingress. 
            <strong className="text-amber-200"> Original files are securely delivered via the Client Dashboard</strong> after invoice verification to ensure systemic integrity.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[9.5px] font-mono text-cyan-400 mt-2 font-bold">
            <span>• CODE PACKS: Independent static auditing is strongly recommended</span>
            <span>• OUTBOUND MAILS: Human reviewer approval is mandatory</span>
          </div>
        </div>
      </div>

      {/* 2. TABBED BAR DESIGN (10 REQUESTED TABS) */}
      <div className="border-b border-zinc-900 pb-1.5 overflow-x-auto scrollbar-thin">
        <div className="flex gap-1.5 min-w-max py-1">
          {[
            { id: 'Digital Product Store', icon: Sparkles, color: 'text-cyan-400' },
            { id: 'Digital Asset Library', icon: FolderOpen, color: 'text-purple-400' },
            { id: 'Website Templates', icon: BookOpen, color: 'text-zinc-400' },
            { id: 'App Templates', icon: Zap, color: 'text-zinc-400' },
            { id: 'Dashboard Templates', icon: Layers, color: 'text-zinc-400' },
            { id: 'UI Kits', icon: FileCheck, color: 'text-zinc-400' },
            { id: 'Slide Decks', icon: TrendingUp, color: 'text-zinc-400' },
            { id: 'Code Packs', icon: Terminal, color: 'text-zinc-400' },
            { id: 'Workflow Templates', icon: Cpu, color: 'text-zinc-400' },
            { id: 'Brand & Social Packs', icon: Building, color: 'text-zinc-400' }
          ].map((tab) => {
            const IconComp = tab.icon;
            const isActive = activeLibraryTab === tab.id;
            const isSpecial = tab.id === 'Digital Asset Library' || tab.id === 'Digital Product Store';
            return (
              <button
                key={tab.id}
                id={`asset-tab-${tab.id.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => {
                  setActiveLibraryTab(tab.id);
                  onAddLog('NAVIGATION', `Changed active library tab to: ${tab.id}`, 'SUCCESS', `Active grid routing updated.`);
                }}
                className={`py-2 px-3 rounded-xl font-mono text-[9px] font-black uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer border ${
                  isActive
                    ? isSpecial 
                      ? 'bg-purple-950/40 text-purple-300 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.25)]'
                      : 'bg-cyan-950/40 text-[#00ffff] border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-zinc-300 hover:bg-[#06060c]'
                }`}
              >
                <IconComp className={`w-3.5 h-3.5 ${tab.color}`} />
                <span>{tab.id}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. CORE PRESENTATION BOX FOR CURRENT TAB */}
      <div className="relative overflow-hidden rounded-2xl border border-purple-500/10 bg-gradient-to-r from-[#01030e] via-[#050b1d] to-[#010207] p-6 shadow-[0_0_40px_rgba(168,85,247,0.06)] flex flex-col md:flex-row gap-6 justify-between items-center">
        <div className="space-y-2 max-w-4xl text-left">
          <div className="flex items-center gap-2">
            <span className="text-[10px] bg-amber-500/10 text-amber-400 font-mono font-black uppercase tracking-widest px-3 py-1 rounded-full border border-amber-500/25">
              👑 NEXUS DIGITAL ASSET LIBRARY
            </span>
            <span className="text-[10px] bg-purple-500/10 text-purple-300 font-mono font-black uppercase tracking-widest px-3 py-1 rounded-full border border-purple-500/25">
              PREMIUM B2B REGISTRY
            </span>
          </div>
          <h2 className="text-xl md:text-2xl font-sans font-black uppercase text-white tracking-tight leading-none">
            {activeLibraryTab === 'Digital Product Store' ? 'Ready-Made Store Solutions' : `NexoraX Template Library › ${activeLibraryTab}`}
          </h2>
          <p className="text-xs text-zinc-400 leading-relaxed font-sans font-light normal-case">
            Browse premium static and deployment digital assets including responsive layout React starters, high-fidelity Figma files, Microsoft Word grant packs, Make.com triggers, and slide presentation models. Deliver directly into the Client Dashboard with managed launch configurations.
          </p>
        </div>

        {/* Administration Switch */}
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => {
              setShowAdminPanel(!showAdminPanel);
              onAddLog('ASSET_LIBRARY', `${showAdminPanel ? 'Closed' : 'Opened'} library administrator panel`, 'SUCCESS', 'Admin authorization query processed.');
            }}
            className={`py-2 px-4 font-mono text-[9px] font-black uppercase tracking-widest rounded-xl border cursor-pointer transition-all flex items-center gap-2 ${
              showAdminPanel 
                ? 'bg-purple-950/60 text-[#e840f3] border-[#e840f3]/40 shadow-[0_0_15px_rgba(232,64,243,0.2)]'
                : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <Cpu className="w-3.5 h-3.5" />
            <span>{showAdminPanel ? 'Close Asset Tool' : 'Asset Admin Tool'}</span>
          </button>
        </div>
      </div>

      {/* 4. ADMIN LIBRARY MANAGER */}
      {showAdminPanel && (
        <div className="bg-[#020511] border-2 border-purple-500/40 rounded-2xl p-6 shadow-[0_0_30px_rgba(168,85,247,0.15)] space-y-6 text-left">
          <div className="flex flex-wrap gap-2 pb-4 border-b border-zinc-900 justify-between items-center">
            <div className="flex items-center gap-2.5">
              <span className="p-2 bg-gradient-to-r from-purple-900 to-indigo-900 text-purple-300 rounded-lg">
                <Cpu className="w-5 h-5 text-purple-400 animate-spin" />
              </span>
              <div>
                <h3 className="text-sm font-mono font-black uppercase text-white">NEXUS DIGITAL ASSET MANAGER Node-07.4</h3>
                <p className="text-[10px] text-zinc-500 font-mono">B2B Inventory drafting queue, license proofs, and external affiliate sync</p>
              </div>
            </div>

            <div className="flex gap-1.5">
              {['add', 'import', 'generator'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setAdminMode(mode as any)}
                  className={`py-1.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase cursor-pointer border transition-all ${
                    adminMode === mode 
                      ? 'bg-purple-500/10 text-purple-400 border-purple-500/40' 
                      : 'bg-black/30 border-zinc-900 text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {mode === 'add' ? 'Add Library Asset' : mode === 'import' ? 'Import Ext. Marketplace' : 'AI Draft Generator'}
                </button>
              ))}
            </div>
          </div>

          {adminSuccessMsg && (
            <div className="bg-emerald-950/20 border border-emerald-500/35 p-3 rounded-lg flex items-center gap-2 text-xs text-emerald-400 font-mono">
              <Check className="w-4 h-4 shrink-0" />
              <span>{adminSuccessMsg}</span>
            </div>
          )}

          {/* Form 1: Add Library Asset */}
          {adminMode === 'add' && (
            <form onSubmit={handleAddCustomAsset} className="space-y-4 font-mono text-xs text-zinc-300">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Asset Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Next.js SaaS Boilerplate"
                    value={newAssetForm.assetName}
                    onChange={(e) => setNewAssetForm({...newAssetForm, assetName: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Asset Type</label>
                  <select
                    value={newAssetForm.assetType}
                    onChange={(e: any) => setNewAssetForm({...newAssetForm, assetType: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Website Template">Website Template</option>
                    <option value="Mobile App Template">Mobile App Template</option>
                    <option value="Dashboard Template">Dashboard Template</option>
                    <option value="UI Kit">UI Kit</option>
                    <option value="Figma File">Figma File</option>
                    <option value="Adobe XD File">Adobe XD File</option>
                    <option value="Sketch File">Sketch File</option>
                    <option value="Code Pack">Code Pack</option>
                    <option value="Workflow Template">Workflow Template</option>
                    <option value="Slide Deck">Slide Deck</option>
                    <option value="Proposal Pack">Proposal Pack</option>
                    <option value="Brand Kit">Brand Kit</option>
                    <option value="Social Pack">Social Pack</option>
                    <option value="Business Pack">Business Pack</option>
                    <option value="Nexus Bundle">Nexus Bundle</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Category Group</label>
                  <input
                    type="text"
                    placeholder="e.g. Design Assets, SaaS Landers"
                    value={newAssetForm.category}
                    onChange={(e) => setNewAssetForm({...newAssetForm, category: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1 md:col-span-2 lg:col-span-3">
                  <label className="text-[10px] text-zinc-400 uppercase">Short Description</label>
                  <input
                    type="text"
                    placeholder="A brief snippet showing in lists"
                    value={newAssetForm.shortDescription}
                    onChange={(e) => setNewAssetForm({...newAssetForm, shortDescription: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1 md:col-span-2 lg:col-span-3">
                  <label className="text-[10px] text-zinc-400 uppercase">Full Pitch &amp; Description</label>
                  <textarea
                    rows={3}
                    placeholder="Detailed marketing presentation copy"
                    value={newAssetForm.fullDescription}
                    onChange={(e) => setNewAssetForm({...newAssetForm, fullDescription: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Holographic Preview Image (URL)</label>
                  <input
                    type="text"
                    value={newAssetForm.previewImage}
                    onChange={(e) => setNewAssetForm({...newAssetForm, previewImage: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Platform (Engine Name)</label>
                  <input
                    type="text"
                    placeholder="e.g. Figma, Framer, Next.js"
                    value={newAssetForm.platform}
                    onChange={(e) => setNewAssetForm({...newAssetForm, platform: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Licence Terms</label>
                  <select
                    value={newAssetForm.licenceType}
                    onChange={(e) => setNewAssetForm({...newAssetForm, licenceType: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Personal Use">Personal Use</option>
                    <option value="Business Use">Business Use</option>
                    <option value="Single Client Use">Single Client Use</option>
                    <option value="Extended Licence">Extended Licence</option>
                    <option value="Agency Licence">Agency Licence</option>
                    <option value="Custom Licence">Custom Licence</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Standard Baseline Price (£)</label>
                  <input
                    type="number"
                    value={newAssetForm.standardPrice}
                    onChange={(e) => setNewAssetForm({...newAssetForm, standardPrice: Number(e.target.value)})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Managed Launch Price (£)</label>
                  <input
                    type="number"
                    value={newAssetForm.managedLaunchPrice}
                    onChange={(e) => setNewAssetForm({...newAssetForm, managedLaunchPrice: Number(e.target.value)})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Source Type Registry</label>
                  <select
                    value={newAssetForm.sourceType}
                    onChange={(e: any) => setNewAssetForm({...newAssetForm, sourceType: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Original NexoraX Asset">Original NexoraX Asset</option>
                    <option value="Licensed Third-Party Asset">Licensed Third-Party Asset</option>
                    <option value="Client Custom Build">Client Custom Build</option>
                    <option value="Draft AI-Generated Asset">Draft AI-Generated Asset</option>
                    <option value="Admin Research Item">Admin Research Item</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Licence Proof Status</label>
                  <select
                    value={newAssetForm.licenceProofStatus}
                    onChange={(e: any) => setNewAssetForm({...newAssetForm, licenceProofStatus: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Original Asset">Original Asset</option>
                    <option value="Licence Stored">Licence Stored</option>
                    <option value="Licence Required">Licence Required</option>
                    <option value="Needs Review">Needs Review</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Asset Layout Level</label>
                  <select
                    value={newAssetForm.setupDifficulty}
                    onChange={(e: any) => setNewAssetForm({...newAssetForm, setupDifficulty: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Beginner">Beginner (Self Setup)</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced (Requires Audit)</option>
                    <option value="Done-For-You Recommended">Done-For-You Recommended</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 pt-5">
                  <input
                    type="checkbox"
                    id="discountEligibleCheck"
                    checked={newAssetForm.discountEligible}
                    onChange={(e) => setNewAssetForm({...newAssetForm, discountEligible: e.target.checked})}
                    className="accent-purple-500 scale-110"
                  />
                  <label htmlFor="discountEligibleCheck" className="text-[10px] text-zinc-300 font-mono font-bold uppercase cursor-pointer select-none">
                    Eligible for 40% Managed Discount
                  </label>
                </div>

              </div>

              {/* PRIVATE ADMIN DIRECTIVE BOX (NEVER DISPLAYED ON CLIENT PDP OVERLAYS) */}
              <div className="border border-purple-500/25 bg-purple-950/20 p-5 rounded-2xl space-y-3.5 mt-2">
                <div className="flex items-center gap-1.5 text-purple-400 uppercase font-black text-[10px]">
                  <ShieldCheck className="w-4 h-4" />
                  <span>🔒 PRIVATE ADMINISTRATIVE LEDGER DIRECTIVES (HIDDEN SECURE STORAGE)</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-[9.5px] text-purple-300 uppercase">Internal Procurement Cost (£)</label>
                    <input
                      type="number"
                      value={newAssetForm.internalCost}
                      onChange={(e) => setNewAssetForm({...newAssetForm, internalCost: Number(e.target.value)})}
                      className="w-full bg-purple-950/30 border border-purple-500/20 rounded-lg p-2 focus:outline-none focus:border-[#e840f3] text-purple-300"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-[9.5px] text-purple-300 uppercase">Margin Estimates (%)</label>
                    <input
                      type="number"
                      value={newAssetForm.marginEstimate}
                      onChange={(e) => setNewAssetForm({...newAssetForm, marginEstimate: Number(e.target.value)})}
                      className="w-full bg-purple-950/30 border border-purple-500/20 rounded-lg p-2 focus:outline-none focus:border-[#e840f3] text-purple-300"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9.5px] text-purple-300 uppercase">File Storage Partition Info</label>
                    <input
                      type="text"
                      value={newAssetForm.fileStorageNotes}
                      onChange={(e) => setNewAssetForm({...newAssetForm, fileStorageNotes: e.target.value})}
                      className="w-full bg-purple-950/30 border border-purple-500/20 rounded-lg p-2 focus:outline-none focus:border-[#e840f3] text-purple-300"
                    />
                  </div>

                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[9.5px] text-purple-300 uppercase">Internal Licensing/Supplier Notes (Private proof agreements)</label>
                    <input
                      type="text"
                      placeholder="e.g. Sublicense contract key NH448796B"
                      value={newAssetForm.licenceNotes}
                      onChange={(e) => setNewAssetForm({...newAssetForm, licenceNotes: e.target.value})}
                      className="w-full bg-purple-950/30 border border-purple-500/20 rounded-lg p-2 focus:outline-none focus:border-[#e840f3] text-purple-300"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9.5px] text-purple-300 uppercase">Legal Review Status</label>
                    <input
                      type="text"
                      disabled
                      value="Auto-vetted Director Node Ingress"
                      className="w-full bg-purple-950/30 border border-purple-500/20 rounded-lg p-2 text-purple-400/70"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  className="py-2.5 px-6 bg-gradient-to-r from-purple-800 to-indigo-700 text-white font-mono font-black uppercase rounded-lg hover:brightness-110 active:scale-95 transition-all cursor-pointer shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                >
                  Publish New Asset Now
                </button>
              </div>
            </form>
          )}

          {/* Form 2: Import External Marketplace Reference */}
          {adminMode === 'import' && (
            <form onSubmit={handleExternalImportSubmit} className="space-y-4 font-mono text-xs text-zinc-300">
              <div className="bg-amber-950/15 border border-amber-500/25 p-4 rounded-xl space-y-2 mb-2">
                <span className="text-amber-400 font-mono font-black uppercase text-[10px] block">
                  ⚠️ STRICT THIRD-PARTY LICENSING DIRECTION (INTELLIGENCE DEPLOYMENT):
                </span>
                <p className="text-[11px] text-zinc-400 leading-relaxed normal-case">
                  Do not copy or resell digital packs from platforms like UI8, Envato, ThemeForest, or Webflow. If NexoraX does own proper resale tokens, list the item strictly as an <strong>External or Affiliate Marketplace Reference</strong>. No direct archive distribution will be offered. Live link buttons are styled with disclaimer disclosures.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">External Core Market Name</label>
                  <select
                    value={externalImportForm.marketplaceName}
                    onChange={(e) => setExternalImportForm({...externalImportForm, marketplaceName: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="UI8 Premium Assets">UI8 Assets Market</option>
                    <option value="ThemeForest Portal">ThemeForest</option>
                    <option value="CodeCanyon Scripts">CodeCanyon</option>
                    <option value="Creative Tim Kits">Creative Tim</option>
                    <option value="Webflow Marketplace">Webflow Templates</option>
                    <option value="Framer Marketplace">Framer Templates</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">External Asset Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Modern Webflow SaaS Portal"
                    value={externalImportForm.assetTitle}
                    onChange={(e) => setExternalImportForm({...externalImportForm, assetTitle: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Affiliate Link / External URL *</label>
                  <input
                    type="url"
                    required
                    placeholder="https://source.com/item-id"
                    value={externalImportForm.externalUrl}
                    onChange={(e) => setExternalImportForm({...externalImportForm, externalUrl: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Asset Category Group</label>
                  <select
                    value={externalImportForm.category}
                    onChange={(e) => setExternalImportForm({...externalImportForm, category: e.target.value})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Design Assets">Design Assets</option>
                    <option value="Website Assets">Website Assets</option>
                    <option value="App Assets">App Assets</option>
                    <option value="Dashboard Assets">Dashboard Assets</option>
                    <option value="Automation Assets">Automation Assets</option>
                    <option value="Presentation Assets">Presentation Assets</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Figma / Zip Formats</label>
                  <input
                    type="text"
                    placeholder="e.g. .fig, .webflow"
                    disabled
                    value=".fig, .zip, link only"
                    className="w-full bg-black/20 border border-zinc-900 rounded-lg p-2 text-zinc-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] text-zinc-400 uppercase">Affiliate Matrix Node</label>
                  <select
                    value={externalImportForm.affiliateStatus}
                    onChange={(e) => setExternalImportForm({...externalImportForm, affiliateStatus: e.target.value as any})}
                    className="w-full bg-black/40 border border-zinc-800 rounded-lg p-2 focus:outline-none focus:border-purple-500 text-white"
                  >
                    <option value="Non-Affiliate Reference">Non-Affiliate Reference Link</option>
                    <option value="Affiliate Marketplace Reference">Affiliate Commission Link (Stripe)</option>
                  </select>
                </div>

              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  className="py-2 px-5 bg-amber-600 text-black font-mono font-black uppercase rounded-lg hover:bg-amber-500 active:scale-95 transition-all cursor-pointer shadow-[0_0_15px_rgba(245,158,11,0.25)]"
                >
                  Catalog External Reference
                </button>
              </div>
            </form>
          )}

          {/* Form 3: Simulated AI Compiler triggers */}
          {adminMode === 'generator' && (
            <div className="space-y-4 font-mono text-zinc-300">
              <div className="bg-purple-950/15 border border-purple-500/25 p-4 rounded-xl">
                <span className="text-purple-400 font-bold uppercase text-[10.5px] block mb-1">
                  ✨ VANGUARD NEURAL COMPILER TERMINAL (AUTONOMOUS INGRESS)
                </span>
                <p className="text-xs text-zinc-400 leading-normal normal-case">
                  Compile fully fleshed UI wireframes, Make workflows, or corporate plan drafts on demand. Generates in "DRAFT" state representing regulatory requirements. Manually review draft specifications before clicking publish.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: "Website Template", type: "Website Template" },
                  { label: "Mobile App Template", type: "Mobile App Template" },
                  { label: "Dashboard Template", type: "Dashboard Template" },
                  { label: "UI Kit Draft", type: "UI Kit" },
                  { label: "Slide Deck Template", type: "Slide Deck" },
                  { label: "Code Pack", type: "Code Pack" },
                  { label: "Make.com Flow", type: "Workflow Template" },
                  { label: "n8n Workflow Draft", type: "Workflow Template" },
                  { label: "Brand Kit Pack", type: "Brand Kit" },
                  { label: "Social Media Pack", type: "Social Pack" },
                  { label: "Business Plan Model", type: "Business Pack" }
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => handleSimulateAiGenerator(item.type)}
                    className="p-3 bg-[#030612] border border-zinc-800 rounded-xl hover:border-purple-500/50 hover:bg-purple-950/25 cursor-pointer text-[#e840f3] text-[9.5px] font-black uppercase text-center transition-all flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5 shrink-0" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5. SEARCH, FILTER & CONTROL GRID NAVBAR */}
      <div className="bg-[#04050e]/60 border border-zinc-900/60 rounded-xl p-4 flex flex-col md:flex-row gap-4 justify-between items-center text-left">
        
        {/* Search */}
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder="Search digital assets collection..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-black/40 border border-zinc-850 rounded-xl text-xs font-mono text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(6,182,212,0.1)]"
          />
        </div>

        {/* Filters and Sorting Trigger */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end font-mono">
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className={`py-2 px-3 bg-zinc-950 rounded-xl text-[10px] font-bold flex items-center gap-2 border cursor-pointer border-zinc-850 hover:text-cyan-400 transition-colors ${isFiltersOpen ? 'text-cyan-400 border-cyan-500/40 bg-cyan-950/10' : 'text-slate-400'}`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Search Parameters</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
          </button>

          <div className="flex items-center gap-2 shrink-0 bg-black/30 border border-zinc-900 rounded-xl px-2.5 py-1.5 bg-zinc-950">
            <span className="text-[9px] text-zinc-500 uppercase font-black">Sort Grid:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-[10px] text-slate-300 focus:outline-none cursor-pointer"
            >
              <option value="priority">Priority Order</option>
              <option value="name">Product Name (A-Z)</option>
              <option value="latest">Latest Added</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Accordion detail filters */}
      {isFiltersOpen && (
        <div className="bg-[#03040c] border border-zinc-900 rounded-xl p-4 font-mono text-xs text-slate-300 text-left grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <div className="space-y-1.5">
            <span className="text-[10px] text-cyan-400 uppercase font-black block">Asset Type:</span>
            <select
              value={selectedAssetType}
              onChange={(e) => setSelectedAssetType(e.target.value)}
              className="w-full bg-black/50 border border-zinc-800 rounded p-1.5 focus:outline-none text-zinc-300 text-[11px]"
            >
              <option value="ALL">ALL Types</option>
              <option value="Website Template">Website Template</option>
              <option value="Mobile App Template">Mobile App Template</option>
              <option value="Dashboard Template">Dashboard Template</option>
              <option value="UI Kit">Figma UI Kit</option>
              <option value="Code Pack">Code Pack</option>
              <option value="Workflow Template">Workflow Template</option>
              <option value="Slide Deck">Slide Deck</option>
              <option value="Brand Kit">Brand Kit / Social</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <span className="text-[10px] text-purple-400 uppercase font-black block">Source Platform:</span>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="w-full bg-black/50 border border-zinc-800 rounded p-1.5 focus:outline-none text-zinc-300 text-[11px]"
            >
              <option value="ALL">ALL Platforms</option>
              {platformsList.map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <span className="text-[10px] text-[#e840f3] uppercase font-black block">Source Provenance:</span>
            <select
              value={selectedSourceType}
              onChange={(e) => setSelectedSourceType(e.target.value)}
              className="w-full bg-black/50 border border-zinc-800 rounded p-1.5 focus:outline-none text-zinc-300 text-[11px]"
            >
              <option value="ALL">ALL Provenances</option>
              <option value="Original NexoraX Asset">Original NexoraX Only</option>
              <option value="External Marketplace Link">Third-Party References</option>
              <option value="Affiliate Marketplace Link">Stripe Affiliates</option>
            </select>
          </div>

          <div className="space-y-1.5 col-span-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-zinc-900/65 flex flex-wrap gap-4 items-center justify-between text-[11px]">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyManagedLaunch}
                  onChange={(e) => setOnlyManagedLaunch(e.target.checked)}
                  className="accent-purple-500 scale-110"
                />
                <span>Managed Launch Eligible Only</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyOriginals}
                  onChange={(e) => setOnlyOriginals(e.target.checked)}
                  className="accent-purple-500 scale-110"
                />
                <span>NexoraX Originals Only</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeExternal}
                  onChange={(e) => setIncludeExternal(e.target.checked)}
                  className="accent-amber-500 scale-110"
                />
                <span>Include External References</span>
              </label>
            </div>

            <div className="flex items-center gap-2 bg-black/30 p-1.5 rounded-lg border border-zinc-900">
              <span>Max Price:</span>
              <span className="text-cyan-400 font-bold">£{maxPrice}</span>
              <input
                type="range"
                min="0"
                max="500"
                step="25"
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="accent-cyan-400 h-1 rounded-lg cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}

      {/* 6. PRIMARY LIBRARY CARDS GRID */}
      {filteredAssets.length === 0 ? (
        <div className="p-16 border border-zinc-900 bg-black/20 rounded-2xl text-center space-y-3">
          <FolderOpen className="w-10 h-10 text-zinc-600 mx-auto animate-bounce" />
          <p className="text-slate-400 text-sm font-light">No premium digital assets match selected filter queries.</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedAssetType('ALL');
              setSelectedPlatform('ALL');
              setSelectedSourceType('ALL');
              setMaxPrice(300);
              setOnlyManagedLaunch(false);
              setOnlyOriginals(false);
              setIncludeExternal(true);
            }}
            className="py-1 px-3 bg-zinc-900 border border-zinc-800 text-[10px] text-cyan-400 rounded-lg hover:text-white hover:bg-zinc-800 font-mono"
          >
            Reset All Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => {
            const isReference = asset.sourceType === "External Marketplace Link" || asset.sourceType === "Affiliate Marketplace Link";
            const isOriginal = asset.sourceType === "Original NexoraX Asset";
            const assetPrice = asset.productPrice || asset.standardPrice || 0;
            const isSaved = watchlist.includes(asset.id);
            const formats = asset.fileFormats || ['.fig'];

            return (
              <div
                key={asset.id}
                id={`asset-card-${asset.id}`}
                className="relative overflow-hidden rounded-2xl bg-[#090515]/85 border-2 border-purple-500/20 hover:border-cyan-500/35 transition-all duration-300 hover:shadow-[0_0_25px_rgba(232,64,243,0.18)] hover:-translate-y-1 p-5 text-left flex flex-col justify-between"
              >
                {/* Image & Badges overlay */}
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden group">
                    <img
                      src={asset.previewImage}
                      alt={asset.productName}
                      className="w-full h-36 object-cover hover:scale-105 duration-500 transition-transform"
                    />
                    
                    {/* Source Origin tag top left */}
                    <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 z-10">
                      {isOriginal ? (
                        <span className="bg-amber-400 text-black text-[8px] font-mono font-black py-0.5 px-2.5 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)]">
                          👑 NEXORAX ORIGINAL
                        </span>
                      ) : (
                        <span className="bg-zinc-900/95 text-zinc-400 border border-zinc-800 text-[8px] font-mono font-black py-0.5 px-2 rounded-full">
                          📦 REFERENCE PORTAL
                        </span>
                      )}
                    </div>

                    {/* Format Badge top right */}
                    <div className="absolute top-2.5 right-2.5 flex gap-1 z-10">
                      {formats.slice(0, 3).map((form) => (
                        <span key={form} className="bg-black/85 text-cyan-400 border border-cyan-500/30 text-[7.5px] font-mono font-black px-1.5 py-0.5 rounded">
                          {form.toUpperCase()}
                        </span>
                      ))}
                    </div>

                    {/* Glow effect overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#02050f]/90 via-transparent to-transparent opacity-60 pointer-events-none" />
                  </div>

                  {/* Asset Core Titles */}
                  <div className="space-y-1">
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-purple-400 text-[9px] font-mono font-bold uppercase tracking-wider block bg-purple-950/40 border border-purple-500/25 px-2 py-0.5 rounded">
                        {asset.assetType || asset.productType}
                      </span>
                      {asset.featured && (
                        <span className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/25 text-[8px] font-mono uppercase px-1.5 py-0.5 rounded flex items-center gap-1.5">
                          Featured Priority
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-sm font-sans font-black text-white uppercase tracking-tight line-clamp-1">
                      {asset.productName}
                    </h3>
                    <p className="text-[11px] text-zinc-400 leading-normal font-sans font-light normal-case line-clamp-2">
                      {asset.shortDescription}
                    </p>
                  </div>

                  {/* Tech stack & Platform detail specifications */}
                  <div className="flex flex-wrap gap-1 pt-1">
                    <span className="bg-black/40 border border-zinc-900 text-[8.5px] font-mono text-zinc-500 px-2 py-0.5 rounded">
                      Platform: <strong className="text-slate-300">{asset.platform || 'B2B Workspace'}</strong>
                    </span>
                    <span className="bg-black/40 border border-zinc-900 text-[8.5px] font-mono text-zinc-500 px-2 py-0.5 rounded">
                      Licence: <strong className="text-slate-300">{asset.licenceType}</strong>
                    </span>
                  </div>

                  {/* Pricing Matrix Layout */}
                  <div className="bg-black/25 border border-zinc-900/60 p-3 rounded-xl space-y-1.5">
                    <div className="flex justify-between items-center text-[11px]">
                      <span className="text-zinc-500">Asset Baseline Cost:</span>
                      <strong className="text-white">
                        {assetPrice === 0 ? 'FREE PREVIEW' : `£${assetPrice}`}
                      </strong>
                    </div>

                    {asset.discountEligible && (
                      <div className="flex justify-between items-center border-t border-zinc-900/60 pt-1.5 text-[10.5px]">
                        <span className="text-purple-400 font-mono text-[9px] uppercase">Managed Launch setup:</span>
                        <div className="text-right">
                          <strong className="text-purple-400">Save 40% Active</strong>
                          <p className="text-[8px] text-zinc-500 leading-none">with NexoraX support</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* CTAs */}
                <div className="space-y-2 pt-4 border-t border-zinc-900/65 mt-4">
                  {isReference ? (
                    <div className="space-y-2">
                      <div className="text-[8.5px] font-mono text-amber-500/80 bg-amber-500/5 p-1 px-2 rounded border border-amber-500/10 mb-2 leading-tight">
                        📢 READ-ONLY REFERENCE: Term policies, purchase routes, and file delivery are administered by external providers.
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSelectedAsset(asset)}
                          className="py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-300 font-mono text-[8.5px] font-bold rounded-lg uppercase hover:bg-zinc-800 cursor-pointer flex items-center justify-center gap-1"
                        >
                          <Eye className="w-3 h-3 text-cyan-400" />
                          <span>View Detail</span>
                        </button>
                        <a
                          href={asset.externalSourceUrl || 'https://themeforest.net'}
                          target="_blank"
                          rel="refererPolicy no-referrer noreferrer"
                          className="py-1.5 bg-amber-600/15 border border-amber-500/30 text-amber-400 font-mono text-[8.5px] font-bold rounded-lg uppercase hover:bg-amber-500/25 cursor-pointer text-center flex items-center justify-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span>External Asset</span>
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        onClick={() => setSelectedAsset(asset)}
                        className="py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-300 font-mono text-[8.5px] font-bold rounded-lg uppercase hover:bg-zinc-800 cursor-pointer flex items-center justify-center gap-1 col-span-1"
                      >
                        <Eye className="w-3 h-3 text-cyan-400" />
                        <span>Preview</span>
                      </button>

                      <button
                        onClick={() => handleRequestSetupInquiry(asset)}
                        className="py-1.5 bg-gradient-to-r from-purple-800 to-indigo-700 text-white font-mono text-[8.5px] font-black rounded-lg uppercase hover:brightness-110 cursor-pointer flex items-center justify-center gap-1 col-span-2 shadow-[0_0_10px_rgba(168,85,247,0.15)]"
                      >
                        <Zap className="w-3 h-3 text-yellow-300" />
                        <span>Request Setup</span>
                      </button>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-[9px] pt-1">
                    <button
                      onClick={() => toggleWatchlist(asset.id, asset.productName)}
                      className="text-zinc-500 hover:text-cyan-400 cursor-pointer flex items-center gap-1 font-mono"
                    >
                      <Bookmark className={`w-3 h-3 ${isSaved ? 'fill-cyan-400 text-cyan-400' : ''}`} />
                      <span>{isSaved ? 'Saved in Watchlist' : 'Watchlist'}</span>
                    </button>

                    <button
                      onClick={() => {
                        onAddLog('ASSET_LIBRARY', `Dispatched asset metadata to remote Pakistan Node: ${asset.productName}`, 'SUCCESS', `Data payload compiled.`);
                        alert(`SENT TO AGENT!\n\nMetadata packet for "${asset.productName}" has been synchronized to NexoraX CloudSync-02 and routed to remote agent "antigravity-preview-05-2026" sandbox.`);
                      }}
                      className="text-zinc-500 hover:text-purple-400 cursor-pointer flex items-center gap-1 font-mono"
                    >
                      <Send className="w-3 h-3" />
                      <span>Send to Agent</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 7. DETAILED POPUP OVERLAY (PDP SCREEN MATRIX) */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-55 flex items-center justify-center p-4">
          <div className="bg-[#03010b] border-2 border-[#e840f3]/45 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar shadow-[0_0_50px_rgba(232,64,243,0.25)] text-left">
            
            {/* Header section with cover image */}
            <div className="relative">
              <img
                src={selectedAsset.previewImage}
                alt={selectedAsset.productName}
                className="w-full h-48 object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#03010b] via-transparent to-transparent" />
              
              <button
                onClick={() => setSelectedAsset(null)}
                className="absolute top-4 right-4 bg-black/80 text-zinc-300 hover:text-white p-2 rounded-full border border-zinc-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="absolute bottom-4 left-6 right-6">
                <span className="text-[10px] font-mono text-[#e840f3] uppercase font-black tracking-widest bg-[#e840f3]/10 px-3 py-1 rounded-full border border-[#e840f3]/35">
                  {selectedAsset.assetType || selectedAsset.productType}
                </span>
                <h3 className="text-xl md:text-2xl font-sans font-black uppercase text-white mt-2 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                  {selectedAsset.productName}
                </h3>
              </div>
            </div>

            {/* Content blocks */}
            <div className="p-6 space-y-6">
              
              {/* Specification layout grids */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
                <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl">
                  <span className="text-[9px] text-zinc-500 block uppercase">Source Platform:</span>
                  <strong className="text-white">{selectedAsset.platform || 'B2B Core'}</strong>
                </div>
                <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl">
                  <span className="text-[9px] text-zinc-500 block uppercase">Licence Policy:</span>
                  <strong className="text-white">{selectedAsset.licenceType}</strong>
                </div>
                <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl">
                  <span className="text-[9px] text-zinc-500 block uppercase">Setup Complexity:</span>
                  <strong className="text-purple-400">{selectedAsset.setupDifficulty}</strong>
                </div>
                <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl">
                  <span className="text-[9px] text-zinc-500 block uppercase">Delivery Vector:</span>
                  <strong className="text-cyan-400">{selectedAsset.deliveryMethod}</strong>
                </div>
              </div>

              {/* Pitch descriptions */}
              <div className="space-y-2">
                <h4 className="text-sm font-sans font-black uppercase tracking-wider text-white">Asset Presentation Pitch:</h4>
                <p className="text-xs text-zinc-300 leading-relaxed font-light normal-case">
                  {selectedAsset.fullDescription}
                </p>
              </div>

              {/* What is Included / Source File Arrays */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-black/30 border border-zinc-900 p-4 rounded-xl space-y-3">
                  <h4 className="text-xs font-mono font-black text-cyan-400 uppercase">📦 INCLUDED BLUEPRINT SOURCE STRUCTURE:</h4>
                  <ul className="text-[11px] text-zinc-400 space-y-1.5 list-disc list-inside">
                    {selectedAsset.includedFiles?.map((file, i) => (
                      <li key={i} className="line-clamp-1">{file}</li>
                    )) || <li>Included files folder layout</li>}
                  </ul>
                </div>

                <div className="bg-black/30 border border-zinc-900 p-4 rounded-xl space-y-3">
                  <h4 className="text-xs font-mono font-black text-purple-400 uppercase">🔌 COMPILED WORKSPACE STACK:</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedAsset.techStack?.map((t) => (
                      <span key={t} className="bg-zinc-950 border border-zinc-900 text-[10px] font-mono text-zinc-300 px-2.5 py-1 rounded">
                        {t}
                      </span>
                    )) || <span className="text-zinc-500 italic">None</span>}
                  </div>
                </div>
              </div>

              {/* Managed Launch Option */}
              {selectedAsset.discountEligible && (
                <div className="bg-purple-950/20 border-2 border-purple-500/35 p-5 rounded-2xl space-y-2.5 text-xs text-zinc-300 normal-case leading-relaxed">
                  <div className="flex items-center gap-2">
                    <span className="p-1 px-3 bg-purple-500 text-white font-mono text-[9px] uppercase font-black rounded-full">
                      ✨ PREMIUM PROVISION: SAVE 40% WITH NEXORAX MANAGED LAUNCH
                    </span>
                  </div>
                  <p>
                    The 40% Managed Launch setup discount applies automatically when domain configurations, SMTP email routing, SSL security provisioning, custom static hosting, and periodic core updates are serviced through the NexoraX staff node portal. 
                    <strong className="text-purple-300"> No client lock-ins apply. No DNS domain names ownership is claimed.</strong> Clients preserve complete regulatory autonomy.
                  </p>
                </div>
              )}

              {/* Public disclosures depending on assetType */}
              <div className="bg-black/50 border border-zinc-900/80 p-4.5 rounded-xl space-y-2 text-zinc-500 text-[10px] leading-relaxed normal-case">
                <div className="flex items-center gap-1.5 text-amber-500/80 uppercase font-bold">
                  <Info className="w-3.5 h-3.5" />
                  <span>Public Safety Regulatory Warnings &amp; Disclosures</span>
                </div>
                
                <p>
                  • Core digital templates require deployment configurations, custom environment credentials (.env secret parameters), database routing bounds, and third-party provider accounts (Make.com, n8n, OpenAI tokens) before final release.
                </p>
                
                {selectedAsset.assetType === 'Code Pack' && (
                  <p className="text-amber-400/80">
                    • CODE PACKAGE DIRECTIVE: This script, schema file structure, or helper is provided strictly as a boilerplate starter baseline. Independent code-base static auditing, verification checks, and vulnerability scanning are highly recommended before binding to real accounts or executing on public ports.
                  </p>
                )}

                {selectedAsset.sourceType?.includes('Marketplace') && (
                  <p className="text-yellow-400/80">
                    • EXTERNAL RESOURCE POLICIES: Purchase, download licensing, and technical assistance are managed solely by external marketplace creators. NexoraX does not claim legal ownership or copyright credentials of third-party assets unless licensed and specified.
                  </p>
                )}
              </div>

              {/* PDP Footers Price and Action Buttons */}
              <div className="flex flex-wrap gap-4 items-center justify-between pt-5 border-t border-zinc-900">
                <div className="space-y-1">
                  <span className="text-[10px] text-zinc-500 uppercase block font-mono">B2B INVOICE QUOTE EST.</span>
                  <div className="flex items-baseline gap-2">
                    <strong className="text-xl text-white">
                      {selectedAsset.productPrice || selectedAsset.standardPrice ? `£${selectedAsset.productPrice || selectedAsset.standardPrice}` : 'FREE PREVIEW'}
                    </strong>
                    {selectedAsset.setupPrice > 0 && (
                      <span className="text-xs text-zinc-500">
                        + £{selectedAsset.setupPrice} setup setup
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2.5">
                  <button
                    onClick={() => {
                      toggleWatchlist(selectedAsset.id, selectedAsset.productName);
                    }}
                    className={`p-2.5 rounded-xl border font-mono text-[9.5px] font-black uppercase cursor-pointer transition-colors ${
                      watchlist.includes(selectedAsset.id)
                        ? 'bg-cyan-900/20 text-cyan-400 border-cyan-500/40'
                        : 'bg-[#030612]/40 border-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    Watchlist
                  </button>

                  <button
                    onClick={() => {
                      const msg = prompt(`Ask a technical or implementation question regarding "${selectedAsset.productName}":`, `Is this boilerplate optimized for containerized environments?`);
                      if (msg) {
                        onAddLog('ASSET_LIBRARY_HELP', `Created assistant query on: ${selectedAsset.productName}`, 'SUCCESS', `User query: "${msg}"`);
                        alert(`ASSISTANT ROUTING:\n\nYour question about "${selectedAsset.productName}" has been synchronized to the Google Drive NexoraX Library. Secure AI assistant or human accountant staff will follow up within standard SLAs.`);
                      }
                    }}
                    className="py-2.5 px-4 bg-zinc-900 border border-zinc-800 text-zinc-300 font-mono text-[9.5px] font-black rounded-xl uppercase hover:bg-zinc-800 cursor-pointer flex items-center gap-1.5"
                  >
                    <HelpCircle className="w-4 h-4 text-purple-400" />
                    <span>Ask Nexus Chat</span>
                  </button>

                  {selectedAsset.sourceType?.includes('Marketplace') ? (
                    <a
                      href={selectedAsset.externalSourceUrl || 'https://themeforest.net'}
                      target="_blank"
                      rel="refererPolicy no-referrer noreferrer"
                      className="py-2.5 px-6 bg-amber-600 text-black font-mono text-[9.5px] font-black rounded-xl uppercase hover:bg-amber-500 cursor-pointer flex items-center gap-1.5 shadow-[0_0_15px_rgba(245,158,11,0.2)]"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>View External Asset</span>
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        const assetToBuy = selectedAsset;
                        setSelectedAsset(null);
                        handleRequestSetupInquiry(assetToBuy);
                      }}
                      className="py-2.5 px-6 bg-gradient-to-r from-purple-800 to-indigo-700 text-white font-mono text-[9.5px] font-black rounded-xl uppercase hover:brightness-110 cursor-pointer flex items-center gap-1.5 shadow-[0_0_20px_rgba(168,85,247,0.3)]"
                    >
                      <Zap className="w-4 h-4 text-yellow-300" />
                      <span>Request Setup</span>
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* 8. CLIENT DASHBOARD DELIVERIES STATUS HUB CONTAINER */}
      <div id="client-asset-deliveries-hub" className="bg-[#04050d] border border-cyan-500/20 rounded-2xl p-6 shadow-[0_0_35px_rgba(6,182,212,0.06)] text-left space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-zinc-900">
          <div className="flex items-center gap-2.5">
            <span className="p-2 bg-cyan-950/40 border border-cyan-500/35 rounded-xl text-cyan-400">
              <FolderOpen className="w-5 h-5 animate-pulse" />
            </span>
            <div>
              <h3 className="text-sm font-mono font-black uppercase text-white tracking-wider">📁 SECURE CLIENT STORAGE DELIVERY</h3>
              <p className="text-[10px] text-zinc-500 font-mono">Original files and setup task schedules for user {user?.email || 'client@nexorax.co.uk'}</p>
            </div>
          </div>

          <span className="text-[10px] bg-cyan-500/10 border border-cyan-500/25 px-3 py-1 rounded-full font-mono text-cyan-400 font-bold uppercase tracking-wider">
            {clientDeliveries.length} Asset(s) Requested
          </span>
        </div>

        {clientDeliveries.length === 0 ? (
          <div className="p-10 text-center space-y-3 bg-black/10 border border-zinc-900 rounded-xl">
            <Info className="w-8 h-8 text-zinc-700 mx-auto" />
            <p className="text-zinc-500 text-xs font-mono normal-case">No asset checkout requests detected in this workspace session.</p>
            <p className="text-[#00ffff] text-[10px] font-mono uppercase">Request setup for original templates above to initialize workspace delivery.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {clientDeliveries.map((delivery) => (
              <div
                key={delivery.id}
                className="bg-black/35 border border-zinc-900 rounded-xl p-4.5 space-y-4"
              >
                <div className="flex flex-wrap justify-between items-start gap-2.5">
                  <div className="space-y-1">
                    <span className="text-[8.5px] bg-[#e840f3]/10 text-[#e840f3] border border-[#e840f3]/25 px-2 py-0.5 rounded uppercase font-mono">
                      {delivery.assetType}
                    </span>
                    <h4 className="text-xs font-mono font-black text-white uppercase">{delivery.assetName}</h4>
                    <p className="text-[10px] text-zinc-500 font-mono">Requested at: {new Date(delivery.requestedAt).toLocaleString()}</p>
                  </div>

                  <span className="text-[9.5px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 font-mono font-black px-3 py-1 roundeduppercase uppercase">
                    Status: {delivery.deliveryStatus}
                  </span>
                </div>

                {/* Checklist steps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-zinc-950/40 p-3.5 rounded-xl border border-zinc-900/60 text-xs">
                  <div className="space-y-2">
                    <span className="text-[9.5px] text-zinc-500 font-mono uppercase block">Setup Checklist progress:</span>
                    <div className="space-y-1.5 text-[10.5px]">
                      {delivery.setupChecklist?.map((step: any, sIdx: number) => (
                        <div key={sIdx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-zinc-600" />
                          <span className="text-zinc-400 font-light">{step.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 font-mono text-[10.5px]">
                    <span className="text-[9.5px] text-zinc-500 uppercase block">Workspace Credentials:</span>
                    <p className="text-zinc-400">🚨 Files delivery access is restricted pending invoice verification checklist approval.</p>
                    <p className="text-purple-400 bg-purple-950/15 border border-purple-500/20 px-2 py-1 rounded inline-block text-[9px] uppercase font-black">
                      Delivery state: Standard client dashboard checkout after approval
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap justify-between items-center text-[10px] pt-1">
                  <div className="flex gap-4 font-mono text-zinc-500">
                    <span>Invoice: <strong className="text-zinc-300">{delivery.invoiceStatus}</strong></span>
                    <span>Support Contract: <strong className="text-purple-400">{delivery.managedLaunchStatus}</strong></span>
                  </div>

                  <button
                    onClick={() => {
                      alert(`SUPPORT CENTER:\n\nDirect calling support triggers for "08000 622827 freephone frequency" are routed. Direct dial setup helper call coordinates mapped for ${delivery.assetName}.`);
                    }}
                    className="text-cyan-400 font-mono hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <span>Contact setup director →</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
